import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { User, UserRoleService } from '../services/role.service';
import { SENTRY_CONTEXT_KEYS, SENTRY_TAG_KEYS } from './sentry-constants';

@Injectable({
  providedIn: 'root',
})
export class SentryContextService implements OnDestroy {
  private initialized = false;
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly userRoleService: UserRoleService, private readonly router: Router) {}

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.userRoleService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => this.updateUserContext(user));

    this.userRoleService.userRole$
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        if (role) {
          Sentry.setTag(SENTRY_TAG_KEYS.role, role);
        }
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        Sentry.setContext(SENTRY_CONTEXT_KEYS.route, { url: event.urlAfterRedirects });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateUserContext(user: User | null): void {
    if (!user) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: String(user.id),
    });

    Sentry.setTag(SENTRY_TAG_KEYS.tenantId, String(user.centerId));
    if (user.centerGroupId !== null) {
      Sentry.setTag(SENTRY_TAG_KEYS.tenantGroupId, String(user.centerGroupId));
    }
    Sentry.setTag(SENTRY_TAG_KEYS.country, user.country);
    Sentry.setTag(SENTRY_TAG_KEYS.currency, user.currency);

    Sentry.setContext(SENTRY_CONTEXT_KEYS.organization, {
      centerId: user.centerId,
      centerGroupId: user.centerGroupId,
      fiscalType: user.fiscalType,
    });

    Sentry.setContext(SENTRY_CONTEXT_KEYS.tenant, {
      id: user.centerId,
      groupId: user.centerGroupId,
      country: user.country,
      currency: user.currency,
    });
  }
}
